import {
  HCP_DOCUMENT_STATUS_VALUES,
  type HcpDocumentStatusValue,
} from "@clipboard-health/contract-documents-service";
import { isDefined } from "@clipboard-health/util-ts";
import { type UpdateWorkerDocumentV2Body } from "@src/appV2/WorkerDocuments/api/useUpdateWorkerDocumentV2";

export function isHcpDocumentStatusValue(value: unknown): value is HcpDocumentStatusValue {
  return (
    typeof value === "string" && (HCP_DOCUMENT_STATUS_VALUES as readonly string[]).includes(value)
  );
}

interface UploadInfoNotification {
  reason?: string | null;
  note?: string | null;
  pushNotification?: boolean | null;
}

interface UploadInfo {
  fulfilledRequirementId?: string;
  displayName?: string;
  status?: HcpDocumentStatusValue;
  expiry?: string | null;
  issueDate?: string | null;
  subType?: string;
  notification?: UploadInfoNotification | null;
}

type UpdateAttributes = NonNullable<UpdateWorkerDocumentV2Body["data"]["attributes"]>;

interface BuildUpdateDocumentV2PayloadArgs {
  uploadInfo: UploadInfo;
  evidenceDefinitionId: string;
  hasLinkedEvidence: boolean;
  isEvidenceApproved: boolean;
  cleanedProperties: Record<string, unknown>;
}

export function buildUpdateDocumentV2Payload(
  args: BuildUpdateDocumentV2PayloadArgs
): UpdateWorkerDocumentV2Body {
  const {
    uploadInfo,
    evidenceDefinitionId,
    hasLinkedEvidence,
    isEvidenceApproved,
    cleanedProperties,
  } = args;
  const includeEvidence = hasLinkedEvidence && isEvidenceApproved;
  const { reason, note, pushNotification } = uploadInfo.notification ?? {};

  const attributes: UpdateAttributes = {
    ...(isDefined(uploadInfo.fulfilledRequirementId) && {
      fulfilledRequirementId: uploadInfo.fulfilledRequirementId,
    }),
    ...(isDefined(uploadInfo.displayName) &&
      uploadInfo.displayName !== "" && { displayName: uploadInfo.displayName }),
    ...(isDefined(uploadInfo.status) && { status: uploadInfo.status }),
    ...(isDefined(uploadInfo.expiry) && { expiry: uploadInfo.expiry }),
    ...(isDefined(uploadInfo.issueDate) && { issueDate: uploadInfo.issueDate }),
    ...(isDefined(uploadInfo.subType) &&
      uploadInfo.subType !== "" && { subType: uploadInfo.subType }),
    ...(isDefined(reason) && { notificationReason: reason }),
    ...(isDefined(note) && { notificationNote: note }),
    ...(isDefined(pushNotification) && { pushNotification }),
    ...(includeEvidence && { properties: cleanedProperties }),
  };

  return {
    data: {
      type: "document",
      attributes,
      ...(includeEvidence && {
        relationships: {
          evidenceDefinition: {
            data: { type: "evidence-definition", id: evidenceDefinitionId },
          },
        },
      }),
    },
  };
}
